import {
  API_CALL,
  API_CALL_FAILURE,
  API_CALL_SUCCESS,
  SET_ACCOMMODATION_V2_SESSIONS,
  SET_ACCOMMODATION_V2_UPGRADE_RECHECK,
} from 'actions/actionConstants'
import { CHECKOUT_ACCOMMODATION_V2_ENQUIRY } from 'actions/apiActionConstants'
import type { AppAction, DefinedAppAction } from 'actions/types/ActionTypes'
import type { ThunkAction } from 'redux-thunk'
import { checkoutEnquiry, createEnquirySession, type CheckoutEnquiryPayload } from 'api/accommodationBooking'
import { removeItems, updateCheckoutItem } from 'actions/CheckoutActions'
import { fetchPropertyRates } from 'actions/PropertyActions'
import {
  buildEnquirySessionPayload,
  buildEnquirySessionPayloadFromCartItem,
  getHotelOnlyPropertyRatePricing,
} from 'lib/accommodation/buildEnquirySessionPayload'
import { buildLoyaltyUpgradeForfeitedItem, buildToggledAccommodationV2Item } from 'checkout/lib/utils/loyalty/buildToggledPropertyRateItem'
import { findRefreshedPropertyRateUpgradeRates } from 'checkout/lib/utils/loyalty/findRefreshedPropertyRateUpgrade'
import {
  clearRejectedPropertyRateUpgrade,
  recordRejectedPropertyRateUpgrade,
  type RejectedUpgradeIdentity,
} from 'checkout/lib/utils/loyalty/rejectedPropertyRateUpgrades'
import getPropertyRatesReduxKeys from 'lib/offer/getPropertyRatesReduxKeys'
import {
  applyAccommodationV2ReplacementSession,
  buildReplacementEnquirySessionPayload,
} from 'lib/accommodation/buildReplacementEnquirySessionPayload'
import {
  getAccommodationV2UpgradeRecheck,
  getAccommodationV2UpgradeRecheckKey,
  hasAccommodationV2SessionError,
  isAccommodationV2PurchaseBlocked,
  isAccommodationV2UpgradeRejected,
} from 'checkout/lib/utils/accommodationV2/outcomes'
import { isAccommodationV2SessionNearExpiry } from 'checkout/lib/utils/accommodationV2/sessionExpiry'
import { isAccommodationV2Item } from 'lib/checkout/checkoutUtils'
import { getFormattedCartItems } from 'checkout/selectors/payment/orderCreationSelectors'
import getPaymentAmount from 'checkout/selectors/payment/getPaymentAmount'
import { getEnquirySubscriber } from 'luxPlus/selectors/checkout/order'
import { getIsLuxPlusLPPEnabled, getLppMemberPricingEnabledForCheckout } from 'luxPlus/selectors/featureToggle'
import type { AppDispatch } from 'src/client/store'
import getOrderLoyalty from 'loyalty/selectors/checkout/order/getOrderLoyalty'
import { hasAccommodationV2PromoInputsChanged } from 'lib/promo/promoMappers'
import settleCheckoutLoyaltyCalculations from 'checkout/actions/loyalty/settleCheckoutLoyaltyCalculations'

// As legacy bedbank: wait this long for refreshed rates before treating the upgrade as lost.
const UPGRADE_RECHECK_RATES_TIMEOUT_MS = 10_000

export interface AccommodationV2PurchasePreparation {
  blocked: boolean
  formattedCartItems: ReturnType<typeof getFormattedCartItems>
  /** Due now for the selected payment option, e.g. a schedule's deposit rather than the full total. */
  paymentAmount: number
  loyalty: ReturnType<typeof getOrderLoyalty>
}

export function setAccommodationV2Sessions(
  sessions: Array<App.AccommodationEnquirySession>,
): AppAction {
  return {
    type: SET_ACCOMMODATION_V2_SESSIONS,
    data: sessions,
  }
}

export interface InitAccommodationV2SessionSelection {
  rate: App.EnrichedPropertyRate
  occupancy: App.Occupants
  selectedOptionalBundles?: Array<Pick<App.Property.OptionalBundle, 'bundleId' | 'tickets'>>
  loyaltyUpgrade?: App.Checkout.PropertyRateLoyaltyUpgrade
}

export interface InitAccommodationV2SessionsParams {
  /** One selection per cart item — rates may differ across multi-room individual mode. */
  selections: Array<InitAccommodationV2SessionSelection>
  checkIn: string
  checkOut: string
  region: string
  /** Shopped LuxPlus+ eligibility. Prices a loyalty upgrade's discount on the member basis. */
  isLuxPlusUser?: boolean
}

/**
 * Creates one enquiry session per selection in parallel.
 *
 * Fail-closed for booking: any create failure rejects the whole attempt so callers
 * abort to sold-out rather than proceeding with a partial set of session ids.
 * Successful creates from a partial failure are still dispatched (so Redux tracks
 * them); the FE has no delete API yet, so those orphans expire via server TTL.
 * Returns session ids in selection order.
 */
export async function initAccommodationV2Sessions(
  dispatch: AppDispatch,
  params: InitAccommodationV2SessionsParams,
): Promise<Array<string>> {
  const state = dispatch((_action: any, getState: () => App.State) => getState())
  // Always a new booking. Checkout has not initialised yet, so `existingOrder` may be stale.
  const lppMemberPricingEnabled = getIsLuxPlusLPPEnabled(state)
  const settled = await Promise.allSettled(params.selections.map(({ rate, occupancy, selectedOptionalBundles, loyaltyUpgrade }) =>
    createEnquirySession(buildEnquirySessionPayload({
      rate,
      occupancy,
      checkIn: params.checkIn,
      checkOut: params.checkOut,
      region: params.region,
      selectedOptionalBundles,
      loyaltyUpgrade,
      ...(params.isLuxPlusUser && { isLuxPlusUser: true }),
      lppMemberPricingEnabled,
    })),
  ))

  const createdSessions = settled.flatMap(result => (
    result.status === 'fulfilled' ? [result.value] : []
  ))
  if (createdSessions.length > 0) {
    dispatch(setAccommodationV2Sessions(createdSessions))
  }

  const firstRejection = settled.find(result => result.status === 'rejected')
  if (firstRejection?.status === 'rejected') {
    throw firstRejection.reason
  }

  return createdSessions.map(session => session.id)
}

/**
 * svc-accommodation prices, reprices and books each session with this membership. Both flags are
 * sent explicitly: an absent `lppMemberPricingEnabled` is treated as on, so a member would be
 * priced at the bedbank member total while svc-order, with the experiment off, books the public one.
 */
function buildCheckoutEnquiryPayload(
  state: App.State,
  sessionIds: Array<string>,
): CheckoutEnquiryPayload {
  return {
    items: sessionIds.map(sessionId => ({ sessionId })),
    transactionKey: state.checkout.cart.transactionKey,
    createdContext: {
      userContext: {
        isLuxPlusUser: !!getEnquirySubscriber(state)?.tier,
        lppMemberPricingEnabled: getLppMemberPricingEnabledForCheckout(state),
      },
    },
  }
}

export function refreshAccommodationV2Sessions(sessionIds: Array<string>): AppAction {
  return (dispatch, getState) => {
    if (sessionIds.length === 0) return

    dispatch({
      type: API_CALL,
      api: CHECKOUT_ACCOMMODATION_V2_ENQUIRY,
      requestedSessionIds: sessionIds,
      request: () => checkoutEnquiry(buildCheckoutEnquiryPayload(getState(), sessionIds)),
    })
  }
}

async function dispatchCheckoutEnquiry(
  dispatch: AppDispatch,
  getState: () => App.State,
  sessionIds: Array<string>,
): Promise<void> {
  if (sessionIds.length === 0) return

  try {
    const data = await checkoutEnquiry(buildCheckoutEnquiryPayload(getState(), sessionIds))
    dispatch({
      type: API_CALL_SUCCESS,
      api: CHECKOUT_ACCOMMODATION_V2_ENQUIRY,
      requestedSessionIds: sessionIds,
      data,
    })
  } catch(error) {
    dispatch({
      type: API_CALL_FAILURE,
      api: CHECKOUT_ACCOMMODATION_V2_ENQUIRY,
      requestedSessionIds: sessionIds,
      error,
    })
  }
}

/**
 * Re-enquires every v2 session on the cart immediately before purchase.
 * Checkout enquiry rematerialises the bedbank supplier session; a stale one
 * fails booking create as `supplier_session_failed`.
 */
export function enquireAccommodationV2BeforePurchase(): ThunkAction<
  Promise<AccommodationV2PurchasePreparation>,
  App.State,
  undefined,
  DefinedAppAction
> {
  return async(dispatch, getState) => {
    const sessionIds = getState().checkout.cart.items
      .filter(isAccommodationV2Item)
      .flatMap(item => item.sessionId ? [item.sessionId] : [])

    const stateBeforeEnquiry = getState()
    await dispatchCheckoutEnquiry(dispatch, getState, sessionIds)

    const { cart, accommodationSessions } = getState().checkout
    // An enquiry that moved a room's price or supplier has the applied promo/vouchers re-priced
    // (promoReapplicationMiddleware). Stop so the customer reviews and pays the re-priced discount.
    const blocked = isAccommodationV2PurchaseBlocked(cart.items, accommodationSessions) ||
      hasAccommodationV2PromoInputsChanged(stateBeforeEnquiry, getState())
    if (!blocked) {
      await settleCheckoutLoyaltyCalculations(dispatch, getState)
    }

    const state = getState()
    return {
      blocked,
      formattedCartItems: getFormattedCartItems(state),
      paymentAmount: getPaymentAmount(state),
      loyalty: getOrderLoyalty(state),
    }
  }
}

/**
 * Whether a toggle can switch back to a side's earlier session rather than mint one: it is for the
 * item's rate, enquired successfully and has time left. A price change is still reusable, as a new
 * session would see it too.
 */
function canReuseAccommodationV2Session(
  item: App.Checkout.AccommodationV2Item,
  record: App.CheckoutState['accommodationSessions'][string] | undefined,
): boolean {
  return !!record &&
    record.data?.data.supplierMetaData.selectedRate.internalRateId === item.internalRateId &&
    !record.fetching &&
    !record.error &&
    !!record.rate &&
    record.outcome !== 'unavailable' &&
    !isAccommodationV2SessionNearExpiry(record)
}

/**
 * Toggle an accomV2 item's loyalty package upgrade: point the item at the other side's rate, reusing
 * that side's earlier session where possible and minting one otherwise. The checkout session hook
 * enquires a new session. On failure the item is left as it was, and the error reaches the caller.
 */
export function toggleAccommodationV2PropertyRateUpgrade(
  item: App.Checkout.AccommodationV2Item,
): ThunkAction<Promise<void>, App.State, undefined, DefinedAppAction> {
  return async(dispatch, getState) => {
    const state = getState()
    const toggled = buildToggledAccommodationV2Item(item)
    if (toggled === item) return

    if (toggled.sessionId && canReuseAccommodationV2Session(toggled, state.checkout.accommodationSessions[toggled.sessionId])) {
      dispatch(updateCheckoutItem(toggled))
      return
    }

    const session = await createEnquirySession(buildEnquirySessionPayloadFromCartItem(
      toggled,
      state.geo.currentRegionCode,
      !!getEnquirySubscriber(state)?.tier,
      state.checkout.cart.items,
      getLppMemberPricingEnabledForCheckout(state),
    ))
    dispatch(setAccommodationV2Sessions([session]))
    dispatch(updateCheckoutItem({ ...toggled, sessionId: session.id }))
  }
}

/**
 * Re-mint an accomV2 item's session so its loyalty package upgrade discount is on the member's
 * current LuxPlus+ basis, as when they join or leave LuxPlus+ at checkout. A session can't change
 * its discount, so this mints one; the checkout session hook enquires it.
 */
export function repriceAccommodationV2UpgradeDiscount(
  item: App.Checkout.AccommodationV2Item,
): ThunkAction<Promise<void>, App.State, undefined, DefinedAppAction> {
  return async(dispatch, getState) => {
    const state = getState()
    const session = await createEnquirySession(buildEnquirySessionPayloadFromCartItem(
      item,
      state.geo.currentRegionCode,
      !!getEnquirySubscriber(state)?.tier,
      state.checkout.cart.items,
      getLppMemberPricingEnabledForCheckout(state),
    ))
    // The item may have moved on while the session was minted; keep its newer state.
    const current = getState().checkout.cart.items.find(cartItem => cartItem.itemId === item.itemId)
    if (!current || !isAccommodationV2Item(current) || current.sessionId !== item.sessionId) return
    dispatch(setAccommodationV2Sessions([session]))
    dispatch(updateCheckoutItem({ ...current, sessionId: session.id }))
  }
}

export function setAccommodationV2UpgradeRecheck(
  key: string,
  recheck: App.AccommodationV2UpgradeRecheck,
): AppAction {
  return {
    type: SET_ACCOMMODATION_V2_UPGRADE_RECHECK,
    key,
    recheck,
  }
}

function getRejectedUpgradeIdentity(
  item: App.Checkout.AccommodationV2Item,
  upgrade: App.Checkout.PropertyRateLoyaltyUpgrade,
): RejectedUpgradeIdentity {
  return {
    propertyId: upgrade.target.propertyId,
    sourceRateId: upgrade.source.serviceRateId,
    targetRateId: upgrade.target.serviceRateId,
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    occupancy: item.occupancy,
  }
}

/**
 * Refetch the property's rates for the item's stay, which also refreshes the offer page's copy.
 * Resolves with the rooms, or undefined when the fetch fails or outlasts the timeout.
 */
function fetchFreshPropertyRooms(
  dispatch: AppDispatch,
  propertyId: string,
  item: App.Checkout.AccommodationV2Item,
): Promise<Array<App.RawPropertyRoomRates> | undefined> {
  const params = {
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    occupancies: [item.occupancy],
    utmContent: undefined,
    roomTypeId: undefined,
    roomRateId: undefined,
  }
  const ratesKey = getPropertyRatesReduxKeys({ propertyId, ...params })?.combined
  if (!ratesKey) return Promise.resolve(undefined)

  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(undefined), UPGRADE_RECHECK_RATES_TIMEOUT_MS)
    dispatch(fetchPropertyRates(propertyId, {
      ...params,
      refresh: true,
      onComplete: (result) => {
        clearTimeout(timeout)
        resolve(result.status === 'success' ? result.data[ratesKey]?.rooms : undefined)
      },
    }))
  })
}

/**
 * Re-check an accomV2 room's applied loyalty package upgrade after its enquiry came back
 * `price_changed`, as legacy bedbank does: refetch availability and look for the same source and
 * target pair. Stores `kept` with the source's refreshed hotel-only pricing, or `lost` when the pair
 * has gone or the rates can't be fetched. A lost upgrade is hidden on the offer page for this stay.
 */
export function recheckAccommodationV2UpgradeAfterPriceChange(
  item: App.Checkout.AccommodationV2Item,
): ThunkAction<Promise<void>, App.State, undefined, DefinedAppAction> {
  return async(dispatch, getState) => {
    const { accommodationSessions, accommodationUpgradeRechecks } = getState().checkout
    const record = item.sessionId ? accommodationSessions[item.sessionId] : undefined
    const upgrade = item.loyaltyPropertyRateUpgrade
    if (!item.sessionId || !record || !upgrade) return
    if (!getAccommodationV2UpgradeRecheck(item, record, accommodationUpgradeRechecks)) return
    const key = getAccommodationV2UpgradeRecheckKey(item.sessionId, record)
    if (!key || accommodationUpgradeRechecks[key]) return

    dispatch(setAccommodationV2UpgradeRecheck(key, { status: 'checking' }))
    const rooms = await fetchFreshPropertyRooms(dispatch, upgrade.target.propertyId, item)
    const refreshed = findRefreshedPropertyRateUpgradeRates(upgrade, rooms)
    const identity = getRejectedUpgradeIdentity(item, upgrade)

    if (!refreshed) {
      recordRejectedPropertyRateUpgrade(identity)
      dispatch(setAccommodationV2UpgradeRecheck(key, { status: 'lost' }))
      return
    }

    clearRejectedPropertyRateUpgrade(identity)
    dispatch(setAccommodationV2UpgradeRecheck(key, {
      status: 'kept',
      sourcePricing: getHotelOnlyPropertyRatePricing(refreshed.sourceRate),
    }))
  }
}

/**
 * The item a price-changed room is re-sessioned as. A kept upgrade is rebuilt on the source's
 * refreshed price; a lost one is forfeited, so the target books at its own price.
 */
function applyAccommodationV2UpgradeRecheck(
  item: App.Checkout.AccommodationV2Item,
  recheck: App.AccommodationV2UpgradeRecheck | undefined,
): App.Checkout.AccommodationV2Item {
  if (!recheck || !item.loyaltyPropertyRateUpgrade) return item
  if (recheck.status === 'checking') {
    throw new Error('Cannot accept a price change while its loyalty upgrade is being re-checked')
  }
  if (recheck.status === 'lost') return buildLoyaltyUpgradeForfeitedItem(item)
  return {
    ...item,
    loyaltyPropertyRateUpgrade: { ...item.loyaltyPropertyRateUpgrade, sourcePricing: recheck.sourcePricing },
  }
}

/**
 * Accepts a mixed checkout-enquiry result: mint replacement sessions for `price_changed`
 * rooms, drop `unavailable` rooms, then re-enquire the survivors.
 */
export function acceptAccommodationV2EnquiryOutcomes(): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const sessions = state.checkout.accommodationSessions
    const items = state.checkout.cart.items.filter(isAccommodationV2Item)
    const isLuxPlusUser = !!getEnquirySubscriber(state)?.tier
    const lppMemberPricingEnabled = getLppMemberPricingEnabledForCheckout(state)

    const priceChangedItems = items.filter(item => (
      item.sessionId && sessions[item.sessionId]?.outcome === 'price_changed'
    ))
    // A rejected loyalty package upgrade keeps the room: the target books at its own price.
    const upgradeRejectedItems = items.filter(item => (
      item.sessionId && isAccommodationV2UpgradeRejected(item, sessions[item.sessionId])
    ))
    const upgradeRejectedItemIds = new Set(upgradeRejectedItems.map(item => item.itemId))
    const unavailableItemIds = new Set(
      items
        .filter(item => item.sessionId && sessions[item.sessionId]?.outcome === 'unavailable')
        .filter(item => !upgradeRejectedItemIds.has(item.itemId))
        .map(item => item.itemId),
    )
    const replacementSessionIds = new Map<string, string>()

    for (const item of upgradeRejectedItems) {
      // Hide the pair on the offer page for this stay, as a legacy bedbank rejection does.
      recordRejectedPropertyRateUpgrade(getRejectedUpgradeIdentity(item, item.loyaltyPropertyRateUpgrade!))
      // The item already carries the target rate's own price; only the discount comes off.
      const forfeited = buildLoyaltyUpgradeForfeitedItem(item)
      const session = await createEnquirySession(buildEnquirySessionPayloadFromCartItem(
        forfeited,
        state.geo.currentRegionCode,
        isLuxPlusUser,
        state.checkout.cart.items,
        lppMemberPricingEnabled,
      ))
      replacementSessionIds.set(item.itemId, session.id)
      dispatch(setAccommodationV2Sessions([session]))
      dispatch(updateCheckoutItem({ ...forfeited, sessionId: session.id }))
    }

    for (const item of priceChangedItems) {
      const record = item.sessionId ? sessions[item.sessionId] : undefined
      if (!record?.data || !record.rate) {
        throw new Error('Cannot accept a price change without a live enquiry rate')
      }
      const replacementItem = applyAccommodationV2UpgradeRecheck(
        item,
        getAccommodationV2UpgradeRecheck(item, record, state.checkout.accommodationUpgradeRechecks),
      )

      const session = await createEnquirySession(buildReplacementEnquirySessionPayload(
        record.data,
        record.rate,
        isLuxPlusUser,
        lppMemberPricingEnabled,
        replacementItem.loyaltyPropertyRateUpgrade,
      ))
      replacementSessionIds.set(item.itemId, session.id)
      dispatch(setAccommodationV2Sessions([session]))
      dispatch(updateCheckoutItem(applyAccommodationV2ReplacementSession(replacementItem, record.rate, session.id)))
    }

    if (unavailableItemIds.size > 0) {
      dispatch(removeItems([...unavailableItemIds]))
    }

    const remainingSessionIds = items.flatMap((item) => {
      if (unavailableItemIds.has(item.itemId)) return []
      const replacementId = replacementSessionIds.get(item.itemId)
      if (replacementId) return [replacementId]
      return item.sessionId ? [item.sessionId] : []
    })

    await dispatchCheckoutEnquiry(dispatch, getState, remainingSessionIds)

    const nextState = getState()
    if (hasAccommodationV2SessionError(nextState.checkout.cart.items, nextState.checkout.accommodationSessions)) {
      throw new Error('Accommodation checkout enquiry refresh failed')
    }

    return {
      stillBlocked: isAccommodationV2PurchaseBlocked(
        nextState.checkout.cart.items,
        nextState.checkout.accommodationSessions,
      ),
    }
  }
}
